import { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';

// material-ui
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import { alpha, useTheme } from '@mui/material/styles';

// ant design icons
import DeleteOutlined from '@ant-design/icons/DeleteOutlined';
import RightOutlined from '@ant-design/icons/RightOutlined';
import DownOutlined from '@ant-design/icons/DownOutlined';

/* The goal rows shared by the ST and OT Daily Activity sheets. The two sheets differ
 * only in how a goal is scored (correct/total vs an assistance level), so the sheet
 * passes that cell in as `renderScore` and everything else - area, goal, activities,
 * controlling prompt, delete - lives here once.
 *
 * Below `md` the table becomes a stack of cards: no horizontal scroll, so a sideways
 * drag inside the sheet is never mistaken for the page's swipe to the next datasheet
 * (the root carries data-swipe-ignore for the same reason). A collapsed card reads as
 * a summary - goal, area, activity count - and opens on a tap anywhere. */

/* ---------- Area combobox ----------
 * Select from the master area list OR type a custom area inline ("+ Add …"). A
 * custom area is stored as its raw text and lives on this sheet only. Known areas
 * are stored as their lowercase key ('rl') — the value the rest of the app (View
 * Data, history) localises — while the combo shows the "RL — Receptive Language"
 * label. */
function AreaCombo({ areaKey, options, onChange, autoFocus, placeholder, addLabel }) {
  const current = options.find((o) => o.key === areaKey) || (areaKey ? areaKey : null);
  return (
    <Autocomplete
      freeSolo
      size="small"
      fullWidth
      options={options}
      value={current}
      selectOnFocus
      handleHomeEndKeys
      isOptionEqualToValue={(o, v) => (typeof v === 'string' ? o.key === v || o.label === v : o.key === v.key)}
      getOptionLabel={(o) => (typeof o === 'string' ? o : o.label || '')}
      filterOptions={(opts, params) => {
        const q = params.inputValue.trim().toLowerCase();
        const filtered = q ? opts.filter((o) => o.label.toLowerCase().includes(q)) : opts.slice();
        const typed = params.inputValue.trim();
        if (typed && !opts.some((o) => o.label.toLowerCase() === typed.toLowerCase())) {
          filtered.push({ key: null, label: addLabel(typed), create: typed });
        }
        return filtered;
      }}
      onChange={(_e, val) => {
        if (val == null) return onChange('');
        if (typeof val === 'string') return onChange(val.trim());
        if (val.create != null) return onChange(val.create);
        return onChange(val.key);
      }}
      renderOption={(props, o) => (
        <li {...props} key={o.key ?? o.label} style={o.create ? { color: '#1a73e8', fontWeight: 500 } : undefined}>
          {o.label}
        </li>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          autoFocus={autoFocus}
          placeholder={placeholder}
          // Same as the goal: a typed custom area is kept when the field loses focus.
          // The shown label of a known area maps back to its key, so an unchanged
          // field is a no-op.
          inputProps={{
            ...params.inputProps,
            onBlur: (e) => {
              params.inputProps.onBlur?.(e);
              const typed = e.target.value.trim();
              const known = options.find((o) => o.label.toLowerCase() === typed.toLowerCase());
              const next = known ? known.key : typed;
              if (next !== (areaKey || '')) onChange(next);
            }
          }}
        />
      )}
    />
  );
}

AreaCombo.propTypes = {
  areaKey: PropTypes.string,
  options: PropTypes.array.isRequired,
  onChange: PropTypes.func.isRequired,
  autoFocus: PropTypes.bool,
  placeholder: PropTypes.string,
  addLabel: PropTypes.func.isRequired
};

/* ---------- Goal combobox ----------
 * Suggests goals already recorded for the selected area (the child's known goal
 * set); with no area it suggests every known goal. Any goal can be typed and added
 * inline ("+ Add …") — a custom goal stays on this sheet only. */
function GoalCombo({ goal, options, onChange, placeholder, addLabel }) {
  return (
    <Autocomplete
      freeSolo
      size="small"
      fullWidth
      options={options}
      value={goal || null}
      selectOnFocus
      handleHomeEndKeys
      isOptionEqualToValue={(o, v) => (typeof o === 'string' ? o === v : false)}
      getOptionLabel={(o) => (typeof o === 'string' ? o : o.label || '')}
      filterOptions={(opts, params) => {
        const q = params.inputValue.trim().toLowerCase();
        const filtered = q ? opts.filter((o) => o.toLowerCase().includes(q)) : opts.slice();
        const typed = params.inputValue.trim();
        if (typed && !opts.some((o) => o.toLowerCase() === typed.toLowerCase())) {
          filtered.push({ create: typed, label: addLabel(typed) });
        }
        return filtered;
      }}
      onChange={(_e, val) => {
        if (val == null) return onChange('');
        if (typeof val === 'string') return onChange(val.trim());
        if (val.create != null) return onChange(val.create);
        return onChange('');
      }}
      renderOption={(props, o) => {
        const isCreate = typeof o !== 'string';
        return (
          <li {...props} key={isCreate ? o.label : o} style={isCreate ? { color: '#1a73e8', fontWeight: 500 } : undefined}>
            {isCreate ? o.label : o}
          </li>
        );
      }}
      // multiline + maxRows: a long goal wraps to at most three lines rather than
      // being clipped. Autocomplete keeps working - the input is still an input,
      // it just grows. minWidth stops the column collapsing on a narrow screen.
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder={placeholder}
          multiline
          maxRows={3}
          sx={{ '& .MuiOutlinedInput-root': { alignItems: 'flex-start' } }}
          // A free-typed goal only reached the row on Enter or on picking "+ Add";
          // clicking away dropped it. Keep what was typed when the field loses focus.
          inputProps={{
            ...params.inputProps,
            onBlur: (e) => {
              params.inputProps.onBlur?.(e);
              const typed = e.target.value.trim();
              if (typed !== (goal || '')) onChange(typed);
            }
          }}
        />
      )}
    />
  );
}

GoalCombo.propTypes = {
  goal: PropTypes.string,
  options: PropTypes.array.isRequired,
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  addLabel: PropTypes.func.isRequired
};

/* Cards up to tablet size, the table on a laptop - the mockups' rule
 * (knowledge/html mockups/Data Sheet): the layout follows the DEVICE (window
 * width), so a laptop keeps the horizontal table whether the sidebar is open or
 * not. Keep 1180 in sync with the mockups. */
const CARD_LAYOUT_QUERY = '(max-width:1180px)';

// Column widths, close to the mockup's, so the table fits a laptop sheet with the
// sidebar open. GOAL takes whatever is left but never less than GOAL_MIN_WIDTH:
// the table carries a matching minWidth, so on the tightest laptop it scrolls
// sideways a little instead of collapsing GOAL to a sliver (its input used to
// overflow into ACTIVITIES).
const COL = { num: 60, area: 190, activities: 110, prompt: 164, del: 48 };
const GOAL_MIN_WIDTH = 240;

const FieldLabel = ({ children }) => (
  <Typography variant="caption" sx={{ display: 'block', mb: 0.75, fontWeight: 600, letterSpacing: '.03em', color: 'text.secondary' }}>
    {children}
  </Typography>
);

FieldLabel.propTypes = { children: PropTypes.node };

const DailyActivityGoalRows = ({
  msgPrefix,
  goals,
  areaOptions,
  goalOptionsFor,
  focusId,
  scoreHeader,
  scoreWidth = 215,
  renderScore,
  renderPromptLevels,
  onToggleExpand,
  onChangeArea,
  onChangeGoal,
  onAddActivity,
  onRemoveActivity,
  onDeleteRow,
  addActivityLabel,
  removeActivityIcon = <DeleteOutlined />
}) => {
  const intl = useIntl();
  const theme = useTheme();
  const isMobile = useMediaQuery(CARD_LAYOUT_QUERY);
  const tableMinWidth = COL.num + COL.area + COL.activities + COL.prompt + COL.del + scoreWidth + GOAL_MIN_WIDTH;
  const t = (key, defaultMessage, values) => intl.formatMessage({ id: `${msgPrefix}.${key}`, defaultMessage }, values);

  // Per-row text of the "add activity" input in the expanded panel.
  const [activityInput, setActivityInput] = useState({});
  // Focus the Area combo of a freshly-added row once, the render it mounts on.
  const focusConsumed = useRef(false);
  useEffect(() => {
    if (focusId != null) focusConsumed.current = false;
  }, [focusId]);

  const addActivity = (id) => {
    const val = String(activityInput[id] || '').trim();
    if (!val) return;
    onAddActivity(id, val);
    setActivityInput((m) => ({ ...m, [id]: '' }));
  };

  const activityCountLabel = (n) =>
    n === 0 ? t('noActivities', 'No activities') : t('activityCount', '{n, plural, one {# activity} other {# activities}}', { n });
  // Built per keystroke: `value` is an ICU argument, and it is only known
  // once something has been typed.
  const addAreaLabel = (value) => t('addNewArea', '+ Add "{value}" as a new area', { value });
  const addGoalLabel = (value) => t('addNewGoal', '+ Add "{value}" as a new goal', { value });

  // Activities for this session: the goal's own list minus any removed for today,
  // plus any added for today.
  const runToday = (g) => [...g.activities.filter((a) => !(g.skippedActivities || []).includes(a)), ...(g.addedActivities || [])];

  const renderAreaCombo = (g, autoFocus) => (
    <AreaCombo
      areaKey={g.area}
      options={areaOptions}
      onChange={(v) => onChangeArea(g.id, v)}
      autoFocus={autoFocus}
      placeholder={t('areaPlaceholder', 'Select or type an area')}
      addLabel={addAreaLabel}
    />
  );

  // No "From IEP · G-xxx" caption: every row starts from Goals & Targets, so the
  // tag told the therapist nothing and cost a line on every row.
  const renderGoalCombo = (g) => (
    <GoalCombo
      goal={g.goal}
      options={goalOptionsFor(g.area)}
      onChange={(v) => onChangeGoal(g.id, v)}
      placeholder={t('goalPlaceholder', 'Select or type a goal')}
      addLabel={addGoalLabel}
    />
  );

  const renderDeleteButton = (g) => (
    <Tooltip title={intl.formatMessage({ id: 'common.delete' })}>
      <IconButton
        size="small"
        color="error"
        onClick={(e) => {
          e.stopPropagation();
          onDeleteRow(g.id);
        }}
        aria-label="Delete row"
      >
        <DeleteOutlined />
      </IconButton>
    </Tooltip>
  );

  /* The expanded panel. Removing an activity here drops it from THIS session's
   * entry only; the sheet decides that (onRemoveActivity), this just lists. The
   * button wording and remove icon come from the sheet - each follows its mockup. */
  const renderActivitiesPanel = (g) => {
    const shown = runToday(g);
    return (
      <Box>
        <FieldLabel>{t('activities', 'Activities').toUpperCase()}</FieldLabel>
        {shown.length === 0 ? (
          <Typography variant="body2" color="text.disabled" sx={{ mb: 1.5 }}>
            {t('noActivitiesYet', 'No activities added yet.')}
          </Typography>
        ) : (
          <Box component="ol" sx={{ m: 0, mb: 1.5, pl: 2.5 }}>
            {shown.map((act, i) => (
              <Box component="li" key={`${i}-${act}`} sx={{ mb: 1, pl: 0.5 }}>
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1}
                  sx={{ bgcolor: 'background.paper', border: 1, borderColor: 'divider', borderRadius: 1, pl: 1.5, pr: 0.5, py: 0.5 }}
                >
                  <Typography variant="body2" sx={{ flex: 1, py: 0.5, whiteSpace: 'pre-wrap', wordBreak: 'break-word', lineHeight: 1.5 }}>
                    {act}
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={() => onRemoveActivity(g.id, act)}
                    aria-label="Remove activity"
                    sx={{ color: 'text.secondary', '&:hover': { color: 'error.main' } }}
                  >
                    {removeActivityIcon}
                  </IconButton>
                </Stack>
              </Box>
            ))}
          </Box>
        )}
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="stretch">
          <TextField
            size="small"
            fullWidth
            value={activityInput[g.id] || ''}
            onChange={(e) => setActivityInput((m) => ({ ...m, [g.id]: e.target.value }))}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                addActivity(g.id);
              }
            }}
            placeholder={t('activityPlaceholder', 'Describe the activity')}
          />
          <Button variant="outlined" onClick={() => addActivity(g.id)} sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}>
            {addActivityLabel}
          </Button>
        </Stack>
      </Box>
    );
  };

  /* Every editable field in the sheet reads as editable: a white fill and a clear
   * border, so it stands out from the tinted expanded row instead of blending into
   * it and looking disabled. Covers the sheet's own score / prompt cells too. */
  const fieldSx = {
    '& .MuiOutlinedInput-root': {
      bgcolor: 'background.paper',
      '& .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.grey[400] },
      '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.primary.light },
      '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.primary.main, borderWidth: 1 }
    },
    '& .MuiOutlinedInput-input, & .MuiSelect-select': { color: 'text.primary' },
    '& .MuiOutlinedInput-input::placeholder': { color: theme.palette.text.secondary, opacity: 1 }
  };

  const expandIcon = (g) => (g.expanded ? <DownOutlined style={{ fontSize: 11 }} /> : <RightOutlined style={{ fontSize: 11 }} />);

  /* ---------- Mobile: one card per goal ---------- */
  if (isMobile) {
    return (
      <Stack spacing={1.25} sx={fieldSx} data-swipe-ignore="true">
        {goals.map((g, idx) => {
          const shouldFocus = g.id === focusId && !focusConsumed.current;
          if (shouldFocus) focusConsumed.current = true;
          const count = runToday(g).length;
          return (
            <Box
              key={g.id}
              onClick={g.expanded ? undefined : () => onToggleExpand(g.id)}
              sx={{
                border: 1,
                borderColor: 'divider',
                borderRadius: 1.5,
                bgcolor: 'background.paper',
                overflow: 'hidden',
                cursor: g.expanded ? 'default' : 'pointer'
              }}
            >
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 1, pt: 1 }}>
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      onToggleExpand(g.id);
                    }}
                    aria-label={g.expanded ? 'Collapse row' : 'Expand row'}
                    sx={{ color: 'text.secondary' }}
                  >
                    {expandIcon(g)}
                  </IconButton>
                  <Typography variant="subtitle2">
                    {intl.formatMessage({ id: 'form.dataSheet.goalNumber', defaultMessage: 'Goal {n}' }, { n: idx + 1 })}
                  </Typography>
                </Stack>
                {renderDeleteButton(g)}
              </Stack>

              {!g.expanded ? (
                <Box sx={{ px: 2, pb: 1.5, pt: 0.5 }}>
                  <Typography
                    variant="body1"
                    fontWeight={500}
                    sx={{ wordBreak: 'break-word' }}
                    color={g.goal ? 'text.primary' : 'text.disabled'}
                  >
                    {g.goal || t('goalPlaceholder', 'Select or type a goal')}
                  </Typography>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1} sx={{ mt: 0.5 }}>
                    <Typography variant="body2" color="text.secondary" noWrap>
                      {areaOptions.find((o) => o.key === g.area)?.label || g.area || '—'}
                    </Typography>
                    <Typography variant="caption" color={count ? 'text.secondary' : 'text.disabled'} sx={{ flexShrink: 0 }}>
                      {activityCountLabel(count)}
                    </Typography>
                  </Stack>
                </Box>
              ) : (
                <>
                  <Stack spacing={1.75} sx={{ px: 2, pb: 2, pt: 1 }}>
                    <Box>
                      <FieldLabel>{t('area', 'Area').toUpperCase()}</FieldLabel>
                      {renderAreaCombo(g, shouldFocus)}
                    </Box>
                    <Box>
                      <FieldLabel>{t('goal', 'Goal').toUpperCase()}</FieldLabel>
                      {renderGoalCombo(g)}
                    </Box>
                    <Box>
                      <FieldLabel>{scoreHeader.toUpperCase()}</FieldLabel>
                      {renderScore(g)}
                    </Box>
                    <Box>
                      <FieldLabel>{t('promptLevel', 'Control Level').toUpperCase()}</FieldLabel>
                      {renderPromptLevels(g)}
                    </Box>
                  </Stack>
                  <Box sx={{ px: 2, py: 2, borderTop: 1, borderColor: 'divider', bgcolor: alpha(theme.palette.primary.main, 0.03) }}>
                    {renderActivitiesPanel(g)}
                  </Box>
                </>
              )}
            </Box>
          );
        })}
      </Stack>
    );
  }

  /* ---------- Desktop / landscape tablet: the table ---------- */
  return (
    <TableContainer sx={{ border: 1, borderColor: 'divider', borderRadius: 1, ...fieldSx }} data-swipe-ignore="true">
      <Table size="small" sx={{ tableLayout: 'fixed', minWidth: tableMinWidth }}>
        <TableHead>
          <TableRow sx={{ '& th': { bgcolor: 'grey.50', fontSize: 12, letterSpacing: '.03em', color: 'text.secondary' } }}>
            <TableCell sx={{ width: COL.num }}>#</TableCell>
            <TableCell sx={{ width: COL.area }}>{t('area', 'Area')}</TableCell>
            <TableCell>{t('goal', 'Goal')}</TableCell>
            <TableCell sx={{ width: COL.activities }}>{t('activities', 'Activities')}</TableCell>
            <TableCell sx={{ width: COL.prompt }}>{t('promptLevel', 'Control Level')}</TableCell>
            <TableCell sx={{ width: scoreWidth }}>{scoreHeader}</TableCell>
            <TableCell sx={{ width: COL.del }} align="right" />
          </TableRow>
        </TableHead>
        <TableBody>
          {goals.map((g, idx) => {
            const shouldFocus = g.id === focusId && !focusConsumed.current;
            if (shouldFocus) focusConsumed.current = true;
            const count = runToday(g).length;
            return [
              <TableRow
                key={g.id}
                sx={g.expanded ? { '& > td': { borderBottom: 'none', bgcolor: alpha(theme.palette.primary.main, 0.03) } } : undefined}
              >
                <TableCell>
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    <IconButton
                      size="small"
                      onClick={() => onToggleExpand(g.id)}
                      aria-label={g.expanded ? 'Collapse row' : 'Expand row'}
                      sx={{ color: 'text.secondary' }}
                    >
                      {expandIcon(g)}
                    </IconButton>
                    <Typography variant="body2" color="text.secondary">
                      {idx + 1}
                    </Typography>
                  </Stack>
                </TableCell>
                <TableCell>{renderAreaCombo(g, shouldFocus)}</TableCell>
                <TableCell>{renderGoalCombo(g)}</TableCell>
                <TableCell>
                  <Typography
                    variant="body2"
                    color={count ? 'text.primary' : 'text.disabled'}
                    onClick={() => onToggleExpand(g.id)}
                    sx={{ whiteSpace: 'nowrap', cursor: 'pointer' }}
                  >
                    {activityCountLabel(count)}
                  </Typography>
                </TableCell>
                <TableCell>{renderPromptLevels(g)}</TableCell>
                <TableCell>{renderScore(g)}</TableCell>
                <TableCell align="right">{renderDeleteButton(g)}</TableCell>
              </TableRow>,
              <TableRow key={`${g.id}-detail`}>
                <TableCell colSpan={7} sx={{ py: 0, border: 0, bgcolor: alpha(theme.palette.primary.main, 0.03) }}>
                  <Collapse in={g.expanded} timeout="auto" unmountOnExit>
                    <Box sx={{ pl: 6, pr: 2, py: 2 }}>{renderActivitiesPanel(g)}</Box>
                  </Collapse>
                </TableCell>
              </TableRow>
            ];
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

DailyActivityGoalRows.propTypes = {
  msgPrefix: PropTypes.string.isRequired,
  goals: PropTypes.array.isRequired,
  areaOptions: PropTypes.array.isRequired,
  goalOptionsFor: PropTypes.func.isRequired,
  focusId: PropTypes.string,
  scoreHeader: PropTypes.string.isRequired,
  scoreWidth: PropTypes.number,
  renderScore: PropTypes.func.isRequired,
  renderPromptLevels: PropTypes.func.isRequired,
  onToggleExpand: PropTypes.func.isRequired,
  onChangeArea: PropTypes.func.isRequired,
  onChangeGoal: PropTypes.func.isRequired,
  onAddActivity: PropTypes.func.isRequired,
  onRemoveActivity: PropTypes.func.isRequired,
  onDeleteRow: PropTypes.func.isRequired,
  addActivityLabel: PropTypes.string.isRequired,
  removeActivityIcon: PropTypes.node
};

export default DailyActivityGoalRows;
